/**
 * What a browser does for free on a multi-page site, and an SPA has to do by
 * hand: give each view a title, put the focus somewhere sensible when the view
 * changes, and say out loud that it changed.
 *
 * Why this exists (found in the M5 accessibility sweep, all three real):
 *
 *  · **Every route had the same `<title>`.** WCAG 2.2 §2.4.2 (Page Titled) is a
 *    Level-A criterion, and in a single-page application each route *is* a
 *    page. It is also the only thing a reader with eight tabs open has to go on.
 *  · **Focus was dropped on the floor at every navigation.** After activating a
 *    link the active element was `<body>`: a screen-reader user heard nothing,
 *    and where the next Tab landed depended on where the removed link had been
 *    in the old document. Focus now moves to the `<main>` landmark, which is
 *    what puts a screen reader at the top of the new view.
 *  · **The scroll position survived the navigation.** Leaving a long report for
 *    the upload screen dropped the reader halfway down a short page. A pushed
 *    navigation now starts at the top; back/forward keeps the browser's own
 *    restoration, which is the behaviour a reader expects there.
 *
 * The focus move is deliberately skipped on the first render — stealing focus
 * from a fresh page load is a bug, not a feature — and the `<main>` element
 * carries `tabIndex={-1}`, so it takes programmatic focus without joining the
 * tab order.
 *
 * It does NOT follow that such a focus draws no ring. That was the assumption
 * here, and it is wrong: Chromium applies `:focus-visible` to a programmatic
 * focus whenever no pointer interaction preceded it — which is exactly the
 * case after `/` redirects to a workspace, or after a sign-in bounces back.
 * The reader then landed on a 2px outline drawn around the whole viewport-tall
 * `<main>`. So the element is marked with `data-route-focus` for the duration
 * of THIS focus only, and the stylesheet suppresses the ring for that one
 * attribute (src/index.css). The marker is dropped again on `blur` and on the
 * first key or pointer event, so the skip link — which focuses the same
 * element, deliberately, by keyboard — still gets its ring, and nothing else
 * in the application loses focus visibility.
 */

/** Set on `<main>` while, and only while, a route change moved focus there. */
export const ROUTE_FOCUS_ATTR = 'data-route-focus'
import { useEffect, useRef, useState } from 'react'

import type { TranslationKey, Translate } from '@/i18n'
import { useRouteMatch, type RouteName } from '@/lib/router'

/** One title key per route. `notFound` included — it is a view like any other. */
export const ROUTE_TITLE: Record<RouteName | 'notFound', TranslationKey> = {
  home: 'title.home',
  signIn: 'title.signIn',
  invite: 'title.invite',
  workspaceIndex: 'title.workspaceIndex',
  workspace: 'title.workspace',
  upload: 'title.upload',
  facts: 'title.facts',
  dossierAudit: 'title.dossierAudit',
  admin: 'title.admin',
  rulebase: 'title.rulebase',
  rulebaseIngest: 'title.rulebaseIngest',
  rulebaseIngestion: 'title.rulebaseIngestion',
  rulebasePack: 'title.rulebasePack',
  run: 'title.run',
  report: 'title.report',
  reportPrint: 'title.reportPrint',
  notFound: 'title.notFound',
}

/** `"Bericht — Baueingabe-Check-KAi"`. The view first, the product second: a tab strip
 *  truncates from the right, and the view is what distinguishes two tabs. */
export function documentTitle(t: Translate, name: RouteName | 'notFound'): string {
  return `${t(ROUTE_TITLE[name])} — ${t('app.name')}`
}

export interface RouteChrome {
  /** Attach to the `<main>` element. */
  mainRef: React.RefObject<HTMLElement | null>
  /** Text for the polite live region; empty until the first navigation. */
  announcement: string
}

export function useRouteChrome(t: Translate): RouteChrome {
  const route = useRouteMatch()
  const mainRef = useRef<HTMLElement | null>(null)
  const previous = useRef<string | null>(null)
  const [announcement, setAnnouncement] = useState('')

  // The print route sets its own title (it names the case and the Gemeinde, so
  // the saved PDF is filed under something a human recognises) — never overrule
  // it from here.
  const owned = route.name !== 'reportPrint'
  const title = documentTitle(t, route.name)

  useEffect(() => {
    if (!owned) return
    document.title = title
  }, [owned, title])

  useEffect(() => {
    const was = previous.current
    previous.current = route.pathname
    if (was === null || was === route.pathname) return
    const main = mainRef.current
    setAnnouncement(title)
    if (!main) return

    main.setAttribute(ROUTE_FOCUS_ATTR, '')
    const clear = () => main.removeAttribute(ROUTE_FOCUS_ATTR)
    main.addEventListener('blur', clear, { once: true })
    // A key or a click means the reader has taken over; from that moment the
    // ring on `<main>` is ordinary focus feedback again and must be visible.
    window.addEventListener('keydown', clear, { once: true })
    window.addEventListener('pointerdown', clear, { once: true })
    main.focus()

    return () => {
      main.removeEventListener('blur', clear)
      window.removeEventListener('keydown', clear)
      window.removeEventListener('pointerdown', clear)
      clear()
    }
  }, [route.pathname, title])

  return { mainRef, announcement }
}
